import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { reportingService } from '../../../services/reportingService';
import WorkOrderDetailModal from './WorkOrderDetailModal';

const DailyWorkOrdersModal = ({ date, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [workOrders, setWorkOrders] = useState([]);
  const [selectedWorkOrderId, setSelectedWorkOrderId] = useState(null);

  useEffect(() => {
    if (date) {
      loadDailyWorkOrders();
    }
  }, [date]);

  const loadDailyWorkOrders = async () => {
    setLoading(true);
    try {
      const data = await reportingService?.getDailyWorkOrders(date);
      setWorkOrders(data);
    } catch (error) {
      console.error('Error loading daily work orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return `$${value?.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr)?.toLocaleDateString('es-MX', { 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const totalRevenue = workOrders?.reduce((sum, order) => sum + parseFloat(order?.total || 0), 0);

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-card rounded-lg shadow-elevation-3 max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-success/10 rounded-lg">
                <Icon name="Calendar" size={24} className="text-success" />
              </div>
              <div>
                <h2 className="text-xl font-heading font-bold text-foreground">
                  {formatDate(date)}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {workOrders?.length} orden{workOrders?.length !== 1 ? 'es' : ''} completada{workOrders?.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <Icon name="X" size={20} className="text-muted-foreground" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <Icon name="Loader2" className="w-8 h-8 text-primary animate-spin" />
              </div>
            ) : (
              <div className="space-y-4">
                {/* Summary */}
                <div className="bg-primary/5 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Ingresos del Día</span>
                    <span className="text-2xl font-bold text-foreground">
                      {formatCurrency(totalRevenue)}
                    </span>
                  </div>
                </div>

                {/* Work Orders List */}
                {workOrders?.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No hay órdenes completadas en esta fecha
                  </p>
                ) : (
                  <div className="space-y-3">
                    {workOrders?.map((order) => (
                      <div
                        key={order?.id}
                        className="bg-muted/30 rounded-lg p-4 hover:bg-muted/50 transition-colors cursor-pointer"
                        onClick={() => setSelectedWorkOrderId(order?.id)}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-semibold text-foreground flex items-center gap-2">
                              <Icon name="FileText" size={16} />
                              OT-{order?.folioOt}
                            </p>
                            <p className="text-sm text-muted-foreground mt-1">
                              {order?.clientName}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-success">
                              {formatCurrency(order?.total)}
                            </p>
                            <Icon name="ChevronRight" size={16} className="text-muted-foreground ml-auto" />
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Icon name="Car" size={14} />
                            {order?.vehicleInfo}
                          </span>
                          <span className="flex items-center gap-1">
                            <Icon name="User" size={14} />
                            {order?.technicianName}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedWorkOrderId && (
        <WorkOrderDetailModal
          workOrderId={selectedWorkOrderId}
          onClose={() => setSelectedWorkOrderId(null)}
        />
      )}
    </>
  );
};

export default DailyWorkOrdersModal;